import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warehouse, Boxes, AlertTriangle, TrendingDown, Printer } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Tier 1 (Feb 2026): Manager "Depot Balance" — current product-wise stock available.
export default function DepotBalance() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState({ depot_total: 0, on_road_total: 0, by_product: [], on_road: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/depot/stock/summary")
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Tier 2.1 (Feb 2026): Print stock report
  const openPrint = async () => {
    try {
      const res = await api.get("/reports/stock.html", { params: { days: 30, lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) toast.error(lang === "ta" ? "பாப்-அப் தடுக்கப்பட்டது" : "Pop-up blocked");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(lang === "ta" ? "பதிவேற்ற முடியவில்ல" : "Print failed");
    }
  };

  const dispName = (it) => (lang === "ta" && it.name_ta) ? it.name_ta : it.name;

  if (loading) return <div className="min-h-screen bg-[#F5F5F7] grid place-items-center text-[#86868B]">{t("loading")}</div>;

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-10">
      <TopBar title={t("depot_balance") || "Depot Balance"} onBack={() => nav("/manager")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Print button */}
        <div className="flex justify-end">
          <button
            data-testid="db-print-btn"
            onClick={openPrint}
            className="px-4 py-2 rounded-xl text-sm font-bold bg-white text-[#0055FF] border border-[#0055FF] hover:bg-[#0055FF] hover:text-white flex items-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" /> {t("print") || "Print"} / PDF
          </button>
        </div>

        {/* Top totals */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-gradient-to-br from-[#5856D6] to-[#0055FF] text-white rounded-3xl p-6 shadow-lg" data-testid="db-depot-total">
            <Warehouse className="w-8 h-8 mb-3 opacity-90" />
            <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">{t("depot_balance")}</div>
            <div className="heading-font text-4xl font-bold mt-1">{data.depot_total || 0}</div>
            <div className="text-sm opacity-90">{t("boxes")}</div>
          </div>
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100" data-testid="db-on-road-total">
            <Boxes className="w-8 h-8 mb-3 text-[#FF9500]" />
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "வழியில் (சேல்ஸ்மென்)" : "On-Road (with team)"}</div>
            <div className="heading-font text-4xl font-bold mt-1 text-[#1D1D1F]">{data.on_road_total || 0}</div>
            <div className="text-sm text-[#86868B]">{t("boxes")}</div>
          </div>
        </div>

        {/* Per-product depot balance */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="heading-font font-bold text-xl">
              {lang === "ta" ? "பொருள்வாரியா சில்லறை" : "Product-wise Balance"}
            </h2>
          </div>
          {data.by_product.length === 0 ? (
            <div className="p-10 text-center text-[#86868B]">
              <Boxes className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <div className="font-bold">{lang === "ta" ? "ஸ்டாக் இல்ல" : "No stock"}</div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.by_product.map((p, idx) => {
                const low = p.qty <= (p.low_stock_threshold || 20);
                return (
                  <li key={p.product_id} className="p-4 flex items-center gap-4" data-testid={`db-product-${idx}`}>
                    <div className={`w-10 h-10 rounded-xl ${low ? "bg-[#FF3B30]/10" : "bg-[#34C759]/10"} grid place-items-center shrink-0`}>
                      {low ? <AlertTriangle className="w-5 h-5 text-[#FF3B30]" /> : <Boxes className="w-5 h-5 text-[#34C759]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold heading-font text-[#1D1D1F] truncate">{dispName(p)}</div>
                      {low && (
                        <div className="text-xs text-[#FF3B30] font-bold mt-0.5 flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" />
                          {lang === "ta" ? "குறைவான ஸ்டாக்" : "Low stock"}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`heading-font text-2xl font-bold ${low ? "text-[#FF3B30]" : "text-[#1D1D1F]"}`}>{p.qty}</div>
                      <div className="text-[10px] text-[#86868B] uppercase tracking-wider font-bold">{t("boxes")}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* On-road per person */}
        {data.on_road.length > 0 && (
          <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <h2 className="heading-font font-bold text-xl">
                {lang === "ta" ? "ஆள்வாரியா வழி ஸ்டாக்" : "Per-Person On-Road"}
              </h2>
            </div>
            <ul className="divide-y divide-gray-100">
              {data.on_road.map((p, idx) => (
                <li key={p.user_id} className="p-4 flex items-center gap-4" data-testid={`db-onroad-${idx}`}>
                  <div className="w-10 h-10 rounded-full bg-[#0055FF]/10 grid place-items-center text-[#0055FF] font-bold heading-font shrink-0">
                    {(p.name || "?")[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold heading-font truncate">{p.name}</div>
                    <div className="text-xs text-[#86868B]">{p.role === "manager" ? (lang === "ta" ? "மேனேஜர்" : "Manager") : (lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="heading-font text-xl font-bold">{p.remaining}</div>
                    <div className="text-[10px] text-[#86868B] uppercase tracking-wider font-bold">{lang === "ta" ? "மீத" : "Remaining"}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
